import type { BillingPlan, Highlight } from './types';
import { FeatureGroup } from './feature-groups';

// Ported from the Ember UI (`app/utils/billing/plans.ts`). The pieces needed by the
// billing-plan transforms are ported here: the plan-type enums, the `Plan` interface,
// and the static `Plans` catalog (used by the Stripe transform to resolve `description`
// and `highlights`). The upgrade-path helpers (`getUpgradePlanTypes`) are also ported —
// see the bottom of this file — now that the billing service's `upgradePlanTypes`
// computed needs them.

export const PlanType = {
  Essentials: 'essentials',
  Basic: 'basic',
  Premium: 'premium',
  Enterprise: 'enterprise',
  Startup: 'startup',
  CdpStarter: 'cdp starter',
  CdpPremium: 'cdp premium',
  NotReportable: 'not reportable',
  Builder: 'builder',
} as const;
export type PlanType = (typeof PlanType)[keyof typeof PlanType];

export type PlanName =
  | 'Essentials'
  | 'Premium'
  | 'Enterprise'
  | 'CdpStarter'
  | 'CdpPremium'
  | 'StartupEssentials'
  | 'StartupBasic'
  | 'StartupPlus';

// Ported from the Ember UI (`app/utils/billing/startup.ts`). Colocated here as the
// Stripe plan transform is its only Hydra consumer so far.
export const StartupType = {
  Essentials: 'Essentials',
  Basic: 'Basic',
  Plus: 'Plus',
} as const;
export type StartupType = (typeof StartupType)[keyof typeof StartupType];

// Ported from the Ember `account` model. Defined here (rather than in Hydra's auth
// layer) because the `Plans` catalog below is the only Hydra consumer so far.
export const PlanFeatures = {
  WarehouseSync: 'warehouseSync',
  Collections: 'collections',
  Workspaces: 'workspaces',
  ObjectTypes: 'objectTypes',
  Messaging: 'messaging',
  MaxObjectJourneys: 'max_object_journeys',
  SupportTierChat: 'support_tier_chat',
  SupportTierCommunity: 'support_tier_community',
  SupportTierEmail: 'support_tier_email',
  PremiumTrial: 'premium_trial',
  CDPPremiumIntegration: 'cdp_premium_integrations',
  AnonymousInApp: 'anonymous_in_app',
  SensitiveAttributes: 'sensitive_attributes',
  HideAllAttributes: 'hide_all_attributes',
  CustomRoles: 'custom_roles',
  DeliverabilityAnalytics: 'deliverabilityAnalytics',
} as const;
export type PlanFeatures = (typeof PlanFeatures)[keyof typeof PlanFeatures];

export const PlanLimits = {
  workspaces: 'workspaces',
  objectTypes: 'objectTypes',
} as const;
export type PlanLimits = (typeof PlanLimits)[keyof typeof PlanLimits];

export interface Plan extends BillingPlan {
  name: string;
  highlightsNewPipelinesObjectsPricing?: Highlight[];
  /** Extra highlights shown to Builder-plan accounts eligible for the self-serve upgrade. */
  builderUpgradeHighlights?: Highlight[];
  description: string;
  planLimits: Partial<Record<PlanLimits, { value: number }>>;
  planFeatures: PlanFeatures[];
  planType: PlanType;
  baseApiCalls?: number;
  slug?: string;
}

export const Plans: Record<PlanName, Plan> = {
  Essentials: {
    name: 'Essentials',
    basePrice: 10000,
    includedCustomers: 5000,
    includedObjects: 500,
    includedMessages: 1000000,
    includedProfiles: 5000,
    customerOverageRate: '0.009',
    objectOverageRate: '0.009',
    profileOverageRate: '0.009',
    messageOverageRate: '0.00012',
    canCancel: true,
    intervalDisplayName: 'monthly',
    highlights: [`5,000 profiles`, `1 million emails per month`, `500 object profiles & 2 object types`],
    highlightsNewPipelinesObjectsPricing: [`5,000 profiles`, `1 million emails per month`, `2 object types`],
    builderUpgradeHighlights: ['Workflows', 'Dynamic segments'],
    description: 'For small companies who are gaining momentum.',
    planLimits: { [PlanLimits.workspaces]: { value: 2 }, [PlanLimits.objectTypes]: { value: 2 } },
    planFeatures: [PlanFeatures.ObjectTypes],
    planType: PlanType.Essentials,
    baseApiCalls: 1000000,
    slug: '202301_pricing_essentials',
    featureGroups: [FeatureGroup.FG1],
    isPaidAnnually: false,
    annualPrice: 0,
    isTrial: false,
    isMauPricing: false,
  },
  Premium: {
    name: 'Premium',
    basePrice: 100000,
    includedCustomers: 50000,
    includedObjects: 5000,
    includedMessages: 1000000,
    includedProfiles: 50000,
    customerOverageRate: '0.007',
    objectOverageRate: '0.009',
    profileOverageRate: '0.007',
    messageOverageRate: '0.00012',
    canCancel: false,
    intervalDisplayName: 'custom',
    highlights: [`Custom profile volume`, `Custom email volume`, `5,000 object profiles & 10 object types`],
    highlightsNewPipelinesObjectsPricing: [`Custom profile volume`, `Custom email volume`, `10 object types`],
    description: 'For end-to-end marketing for high-growth companies.',
    planLimits: { [PlanLimits.workspaces]: { value: 0 }, [PlanLimits.objectTypes]: { value: 10 } },
    planFeatures: [PlanFeatures.WarehouseSync, PlanFeatures.Collections, PlanFeatures.ObjectTypes],
    planType: PlanType.Premium,
    baseApiCalls: 1000000,
    featureGroups: [FeatureGroup.FG2],
    isPaidAnnually: false,
    annualPrice: 0,
    isTrial: false,
    isMauPricing: false,
  },
  Enterprise: {
    name: 'Enterprise',
    basePrice: 0,
    includedCustomers: 0,
    includedObjects: 5000,
    includedProfiles: 0,
    includedMessages: 3000000,
    customerOverageRate: '0',
    objectOverageRate: '0.009',
    profileOverageRate: '0',
    messageOverageRate: '0.00012',
    canCancel: false,
    intervalDisplayName: 'custom',
    highlights: [`Custom profile volume`, `Custom email volume`, `5,000 object profiles & 10 object types`],
    highlightsNewPipelinesObjectsPricing: [`Custom profile volume`, `Custom email volume`, `10 object types`],
    description: 'For larger companies scaling lifecycle marketing.',
    planLimits: { [PlanLimits.workspaces]: { value: 0 }, [PlanLimits.objectTypes]: { value: 10 } },
    planFeatures: [PlanFeatures.WarehouseSync, PlanFeatures.Collections, PlanFeatures.ObjectTypes],
    planType: PlanType.Enterprise,
    baseApiCalls: 1000000,
    featureGroups: [FeatureGroup.FG3],
    isPaidAnnually: false,
    annualPrice: 0,
    isTrial: false,
    isMauPricing: false,
  },
  CdpStarter: {
    name: 'Starter',
    basePrice: 0,
    includedCustomers: 0,
    includedObjects: 0,
    includedProfiles: 0,
    includedMessages: 0,
    customerOverageRate: '0',
    objectOverageRate: '0',
    profileOverageRate: '0',
    messageOverageRate: '0',
    canCancel: true,
    intervalDisplayName: 'monthly',
    highlights: [`1 million monthly API calls`, `Unlimited sources`, `Unlimited destinations`, `2 workspaces`],
    description: 'Centralize customer data',
    planLimits: { [PlanLimits.workspaces]: { value: 2 } },
    planFeatures: [],
    planType: PlanType.CdpStarter,
    baseApiCalls: 1000000,
    slug: 'cdp_starter_202304',
    featureGroups: [FeatureGroup.FG6],
    isPaidAnnually: false,
    annualPrice: 0,
    isTrial: false,
    isMauPricing: false,
  },
  CdpPremium: {
    name: 'Premium',
    basePrice: 0,
    includedCustomers: 0,
    includedObjects: 0,
    includedProfiles: 0,
    includedMessages: 0,
    customerOverageRate: '0',
    objectOverageRate: '0',
    profileOverageRate: '0',
    messageOverageRate: '0',
    canCancel: true,
    intervalDisplayName: 'monthly',
    highlights: [
      `10 million+ monthly API calls`,
      `Unlimited sources`,
      `Unlimited destinations`,
      `Unlimited workspaces`,
    ],
    description: 'A fully-featured CDP that scales',
    planLimits: { [PlanLimits.workspaces]: { value: 0 } },
    planFeatures: [],
    planType: PlanType.CdpPremium,
    baseApiCalls: 1000000,
    featureGroups: [FeatureGroup.FG6],
    isPaidAnnually: false,
    annualPrice: 0,
    isTrial: false,
    isMauPricing: false,
  },
  StartupEssentials: {
    name: 'Startup Essentials',
    basePrice: 10000,
    includedCustomers: 5000,
    includedObjects: 500,
    includedProfiles: 5000,
    includedMessages: 1000000,
    customerOverageRate: '0.009',
    objectOverageRate: '0.009',
    profileOverageRate: '0.009',
    messageOverageRate: '0.00012',
    canCancel: true,
    intervalDisplayName: 'monthly',
    highlights: [
      '5,000 profiles',
      'Send Emails, Push, In-App, and SMS',
      'Collections',
      'Customer.io Community Support',
      '$1,200 value',
    ],
    description: 'Startup essentials includes everything you need to start sending messages to your customers',
    planLimits: { [PlanLimits.workspaces]: { value: 2 }, [PlanLimits.objectTypes]: { value: 2 } },
    planFeatures: [PlanFeatures.ObjectTypes],
    planType: PlanType.Essentials,
    baseApiCalls: 1000000,
    slug: 'plan_NTh5DaIQSCHFBH',
    featureGroups: [FeatureGroup.FG9],
    isPaidAnnually: false,
    annualPrice: 0,
    isTrial: false,
    isMauPricing: false,
  },
  StartupBasic: {
    name: 'Startup Basic',
    basePrice: 15000,
    includedCustomers: 12000,
    includedObjects: 500,
    includedProfiles: 12000,
    includedMessages: 1000000,
    customerOverageRate: '0.005',
    objectOverageRate: '0.009',
    profileOverageRate: '0.005',
    messageOverageRate: '0.00012',
    canCancel: true,
    intervalDisplayName: 'monthly',
    highlights: [
      '12,000 profiles',
      'Send Emails, Push, In-App, and SMS',
      'Collections',
      'Customer.io Community Support',
      '$1,800 value',
    ],
    description: 'Startup basic includes everything you need to start sending messages to your customers',
    planLimits: { [PlanLimits.workspaces]: { value: 0 }, [PlanLimits.objectTypes]: { value: 2 } },
    planFeatures: [PlanFeatures.WarehouseSync, PlanFeatures.Collections, PlanFeatures.ObjectTypes],
    planType: PlanType.Startup,
    baseApiCalls: 1000000,
    slug: '202303_startup_basic_static',
    featureGroups: [FeatureGroup.FG4],
    isPaidAnnually: false,
    annualPrice: 0,
    isTrial: false,
    isMauPricing: false,
  },
  StartupPlus: {
    name: 'Startup +Plus',
    basePrice: 100000,
    includedCustomers: 100000,
    includedObjects: 5000,
    includedProfiles: 100000,
    includedMessages: 1000000,
    customerOverageRate: '0.004',
    objectOverageRate: '0.009',
    profileOverageRate: '0.004',
    messageOverageRate: '0.00012',
    canCancel: false,
    intervalDisplayName: 'monthly',
    highlights: [
      'Includes 100,000 profiles',
      '10 object types',
      'Collections',
      'Limited technical support',
      '1-hour onboarding and planning call',
      '$12,000 value',
    ],
    description: 'Startup +Plus includes all basic features plus additional technical and onboarding support',
    planLimits: { [PlanLimits.workspaces]: { value: 0 }, [PlanLimits.objectTypes]: { value: 10 } },
    planFeatures: [PlanFeatures.WarehouseSync, PlanFeatures.Collections, PlanFeatures.ObjectTypes],
    planType: PlanType.Startup,
    baseApiCalls: 1000000,
    slug: 'plan_H4CyyclaRROnke',
    featureGroups: [FeatureGroup.FG5],
    isPaidAnnually: false,
    annualPrice: 0,
    isTrial: false,
    isMauPricing: false,
  },
};

/**
 * Which product app an upgrade path targets. Ported from the Ember UI
 * (`app/utils/product-app-info.ts`); only the two values the upgrade helpers below use are
 * ported here. Defined as a const object (not an enum) per the Hydra type conventions. The
 * string values match the Ember `ProductApp` enum so the {@link getUpgradePlanTypes} record
 * keys are identical across the two apps.
 */
export const ProductApp = {
  Journeys: 'journeys',
  DataPipelines: 'pipelines',
} as const;
export type ProductApp = (typeof ProductApp)[keyof typeof ProductApp];

/**
 * The plan types on Customer.io's current offerings (as opposed to legacy plans); used by the
 * contracts-sales sidebar to route a request to the right inbox and to pick the right greeting.
 */
export const currentOfferingsByPlanType: PlanType[] = [
  PlanType.Startup,
  PlanType.Essentials,
  PlanType.Premium,
  PlanType.Enterprise,
  PlanType.CdpStarter,
  PlanType.CdpPremium,
];

// Upgrade-path lookup tables, ported verbatim from the Ember `getUpgradePlanTypes`. Typed as
// `Record<string, PlanType>` (rather than an exhaustive `Record<PlanType, PlanType>`) so a
// plan type absent from the table resolves to `undefined` at runtime exactly as it did in
// Ember, without forcing an entry for every plan type.
const JourneysUpgradePlan: Record<string, PlanType> = {
  [PlanType.Essentials]: PlanType.Premium,
  [PlanType.Premium]: PlanType.Enterprise,
  [PlanType.Enterprise]: PlanType.Enterprise,
  [PlanType.CdpStarter]: PlanType.Essentials,
  [PlanType.CdpPremium]: PlanType.Essentials,
};

const DataPipelinesUpgradePlan: Record<string, PlanType> = {
  [PlanType.Essentials]: PlanType.CdpStarter,
  [PlanType.Premium]: PlanType.CdpStarter,
  [PlanType.Enterprise]: PlanType.CdpStarter,
  [PlanType.CdpStarter]: PlanType.CdpPremium,
  [PlanType.CdpPremium]: PlanType.CdpPremium,
};

/**
 * Resolves the Journeys and Data Pipelines upgrade targets for the current plan. Ported from
 * the Ember UI (`app/utils/billing/plans.ts`). A premium trial always upgrades to Premium; a
 * Journeys (messaging) trial upgrades to Essentials; otherwise the upgrade follows the lookup
 * tables above (defaulting to Essentials / CDP Starter when the current plan type is unknown).
 */
export function getUpgradePlanTypes(
  isPremiumTrial: boolean,
  isJourneysTrial: boolean,
  currentPlanType: PlanType | undefined,
): Record<ProductApp, PlanType> {
  let journeysUpgradePlan = currentPlanType != null ? JourneysUpgradePlan[currentPlanType] : Plans.Essentials.planType;
  const dataPipelinesUpgradePlan =
    currentPlanType != null ? DataPipelinesUpgradePlan[currentPlanType] : Plans.CdpStarter.planType;

  if (isPremiumTrial) {
    journeysUpgradePlan = Plans.Premium.planType;
  } else if (isJourneysTrial) {
    journeysUpgradePlan = Plans.Essentials.planType;
  }

  return {
    [ProductApp.Journeys]: journeysUpgradePlan,
    [ProductApp.DataPipelines]: dataPipelinesUpgradePlan,
  };
}
